import React, { FC } from "react";
import { Modal, notification } from "antd";
import { useIntl } from "react-intl";
import { AveliosButton } from "../common-layout/avelios-button";
import {
  AppointmentProxyFragment,
  AppointmentProxyFragmentDoc,
  Resource_AppointmentSlotProposalMsg,
  Resource_AppointmentStatus,
  useAcceptAppointmentProposalMutation,
} from "../../src/generated/graphql";
import { Maybe } from "graphql/jsutils/Maybe";
import { buildTimestampDateTime } from "../../helpers/formatters";
import { useCurrentUser } from "../../helpers/utils";

interface AcceptAppointmentProposalModalProps {
  open: boolean;
  onClose: () => void;
  appointmentSlotProposal:
    | Maybe<Resource_AppointmentSlotProposalMsg>
    | undefined;
  clinicId: Maybe<string> | undefined;
}

export const AcceptAppointmentProposalModal: FC<
  AcceptAppointmentProposalModalProps
> = ({ open, onClose, appointmentSlotProposal, clinicId }) => {
  const { formatMessage } = useIntl();

  const { currentUser } = useCurrentUser();

  const proposedAppointmentSlotId = appointmentSlotProposal?.id;

  const [acceptAppointmentProposalMutation] =
    useAcceptAppointmentProposalMutation({
      update(cache, { data }) {
        const appointmentRequest =
          data?.acceptAppointmentProposal?.appointmentRequest;
        const clinicId = data?.acceptAppointmentProposal?.clinicId;
        const portalFacilityPath =
          data?.acceptAppointmentProposal?.portalFacilityPath;
        const appointment = appointmentRequest?.appointments?.[0];

        if (
          appointment?.appointmentStatus ===
          Resource_AppointmentStatus.AppointmentStatusBooked
        ) {
          cache.modify({
            fields: {
              getPortalUserAppointments(previousAppointments = {}) {
                const newAppointmentProxyRef =
                  cache.writeFragment<AppointmentProxyFragment>({
                    data: {
                      clinicId,
                      appointment: appointment,
                      portalFacilityPath,
                      __typename: "patientportal_AppointmentProxyMsg",
                    },
                    id: appointment!.id!,
                    fragment: AppointmentProxyFragmentDoc,
                    fragmentName: "AppointmentProxy",
                  });

                const previousProxyAppointments =
                  previousAppointments?.proxyAppointments || [];

                return {
                  ...previousAppointments,
                  proxyAppointments: [
                    newAppointmentProxyRef,
                    ...previousProxyAppointments,
                  ],
                };
              },
            },
          });
        }
      },
      onCompleted: (data) => {
        const appointment =
          data.acceptAppointmentProposal?.appointmentRequest?.appointments?.[0];
        if (
          appointment?.appointmentStatus ===
          Resource_AppointmentStatus.AppointmentStatusBooked
        ) {
          notification.success({
            message: formatMessage({
              id: "slot-selection.selected-slot.booking-successful",
            }),
          });
          onClose();
        } else {
          notification.error({
            message: formatMessage({ id: "notification.error" }),
          });
        }
      },
    });

  const acceptAppointmentProposal = () => {
    if (proposedAppointmentSlotId && clinicId && currentUser?.id) {
      acceptAppointmentProposalMutation({
        variables: {
          appointmentSlotProposalId: proposedAppointmentSlotId,
          clinicId,
          portalUserId: currentUser.id,
        },
      });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={formatMessage({ id: "appointment.proposal.accept.title" })}
      footer={
        <AveliosButton
          disabled={!proposedAppointmentSlotId || !clinicId}
          type={"primary"}
          onClick={acceptAppointmentProposal}
        >
          {formatMessage({ id: "appointment.proposal.accept" })}
        </AveliosButton>
      }
    >
      <p>
        {formatMessage(
          { id: "appointment.proposal.accept.description" },
          { date: buildTimestampDateTime(appointmentSlotProposal?.startTime) }
        )}
      </p>
    </Modal>
  );
};
